import React, { useMemo } from 'react'
import {
  Alert,
  Box,
  Button,
  Chip,
  Collapse,
  IconButton,
  Stack,
  Typography,
} from '@mui/material'
import {
  CheckCircle,
  Cancel,
  Translate,
  PictureAsPdf,
  ExpandMore,
  ExpandLess,
  ContentCopy,
  SkipNext,
  Warning,
} from '@mui/icons-material'
import { SummaryTable, SummaryTableRow } from 'components/SummaryTable'
import { Table } from 'shared/ui/Table/Table'
import { Column } from 'shared/ui/Table/types'
import { copyToClipboard } from 'shared/utils'
import {
  PdfTranslationResultJob,
  PdfTranslationResult,
  PdfTranslationError,
  PdfTranslationOverflowBlock,
} from './types'

type OverflowRow = PdfTranslationOverflowBlock & {
  pdf_name: string
  original_url: string
}

function extractFileName(url: string): string {
  try {
    const parsedUrl = new URL(url)
    const parts = parsedUrl.pathname.split('/').filter(Boolean)
    return decodeURIComponent(parts[parts.length - 1] ?? url)
  } catch {
    return url
  }
}

function getSummaryRows(
  summary: PdfTranslationResultJob['data']['summary'],
): SummaryTableRow[] {
  return [
    { cells: ['Total Unique PDFs', summary.total_unique_pdfs] },
    { cells: ['To Translate', summary.total_to_translate] },
    { cells: ['Translated', summary.total_translated] },
    { cells: ['Failed', summary.total_failed] },
    { cells: ['Skipped', summary.total_skipped] },
    { cells: ['Errors', summary.total_errors] },
  ]
}

function getTelemetrySummary(results: PdfTranslationResult[]) {
  return results.reduce(
    (acc, r) => ({
      totalTokens: acc.totalTokens + (r.telemetry.total_tokens ?? 0),
      promptTokens: acc.promptTokens + (r.telemetry.prompt_tokens ?? 0),
      completionTokens:
        acc.completionTokens + (r.telemetry.completion_tokens ?? 0),
      totalBlocks: acc.totalBlocks + r.blocks_extracted,
      totalPages: acc.totalPages + r.pages,
    }),
    {
      totalTokens: 0,
      promptTokens: 0,
      completionTokens: 0,
      totalBlocks: 0,
      totalPages: 0,
    },
  )
}

/**
 * Renders the results of a PDF tree translation job.
 * Displays translation summary, errors, translated PDFs table, and overflow blocks.
 * @param result - The PDF translation job result
 */
export function PdfTranslationResultResponse({
  result,
}: {
  result: PdfTranslationResultJob
}) {
  const { data, error } = result
  const { summary, translation_results, errors, skipped_urls } = data ?? {}
  const telemetry = useMemo(
    () => getTelemetrySummary(translation_results ?? []),
    [translation_results],
  )
  const overflowCount = useMemo(
    () =>
      (translation_results ?? []).reduce(
        (acc, r) => acc + r.overflow_blocks.length,
        0,
      ),
    [translation_results],
  )

  if (error) {
    const errorMessage =
      typeof error === 'object' && error !== null && 'message' in error
        ? String((error as { message?: unknown }).message)
        : typeof error === 'string'
          ? error
          : String(error)

    return (
      <Box padding="10px">
        <Alert severity="error">
          <strong>ERROR:</strong> {errorMessage}
        </Alert>
      </Box>
    )
  }

  if (!summary) return null

  return (
    <Box>
      <TranslationSummary
        summary={summary}
        telemetry={telemetry}
        overflowCount={overflowCount}
      />

      {errors && errors.length > 0 && <ErrorsSection errors={errors} />}

      {skipped_urls && skipped_urls.length > 0 && (
        <SkippedUrlsSection urls={skipped_urls} />
      )}

      {translation_results && translation_results.length > 0 && (
        <TranslationResultsTable results={translation_results} />
      )}

      {overflowCount > 0 && translation_results && (
        <OverflowBlocksSection results={translation_results} />
      )}
    </Box>
  )
}

function TranslationSummary({
  summary,
  telemetry,
  overflowCount,
}: {
  summary: PdfTranslationResultJob['data']['summary']
  telemetry: ReturnType<typeof getTelemetrySummary>
  overflowCount: number
}) {
  return (
    <Box padding="10px">
      <Typography variant="h6" gutterBottom>
        PDF Translation Summary
      </Typography>
      <Stack direction="row" spacing={1} flexWrap="wrap" gap={1} mb={2}>
        <Chip
          icon={<PictureAsPdf />}
          label={`${summary.total_unique_pdfs} PDFs`}
          color="primary"
          size="small"
        />
        <Chip
          icon={<CheckCircle />}
          label={`${summary.total_translated} Translated`}
          color="success"
          size="small"
        />
        {summary.total_failed > 0 && (
          <Chip
            icon={<Cancel />}
            label={`${summary.total_failed} Failed`}
            color="error"
            size="small"
          />
        )}
        {summary.total_skipped > 0 && (
          <Chip
            icon={<SkipNext />}
            label={`${summary.total_skipped} Skipped`}
            color="warning"
            size="small"
          />
        )}
        <Chip
          icon={<Translate />}
          label={`${telemetry.totalTokens.toLocaleString()} tokens`}
          color="default"
          size="small"
        />
        {overflowCount > 0 && (
          <Chip
            icon={<Warning />}
            label={`${overflowCount} Overflow Blocks`}
            color="warning"
            size="small"
          />
        )}
      </Stack>
      <SummaryTable
        rows={getSummaryRows(summary)}
        firstColumnSx={{ fontStyle: 'italic' }}
      />
      <Box mt={1}>
        <Typography variant="body2" color="text.secondary">
          Total Pages: {telemetry.totalPages.toLocaleString()} | Total Blocks:{' '}
          {telemetry.totalBlocks.toLocaleString()} | Prompt Tokens:{' '}
          {telemetry.promptTokens.toLocaleString()} | Completion Tokens:{' '}
          {telemetry.completionTokens.toLocaleString()}
        </Typography>
      </Box>
    </Box>
  )
}

function ErrorsSection({ errors }: { errors: PdfTranslationError[] }) {
  const errorGroups = useMemo(() => {
    const groups: Record<string, { count: number; urls: string[] }> = {}
    for (const err of errors) {
      const key = err.error.value
      if (!groups[key]) groups[key] = { count: 0, urls: [] }
      groups[key].count++
      groups[key].urls.push(err.pdf_url)
    }
    return Object.entries(groups).sort((a, b) => b[1].count - a[1].count)
  }, [errors])

  const errorColumns: Column<PdfTranslationError>[] = [
    {
      title: 'PDF',
      rowContent: ({ pdf_url }) => (
        <Typography
          variant="body2"
          component="a"
          href={pdf_url}
          target="_blank"
          rel="noopener noreferrer"
          sx={{ wordBreak: 'break-word' }}
        >
          {extractFileName(pdf_url)}
        </Typography>
      ),
    },
    {
      title: 'Error Type',
      rowContent: ({ error }) => (
        <Typography variant="body2" color="error">
          {error.type}
        </Typography>
      ),
    },
    {
      title: 'Error Message',
      rowContent: ({ error }) => (
        <Typography variant="body2" sx={{ wordBreak: 'break-word' }}>
          {error.value}
        </Typography>
      ),
    },
    {
      title: 'Copy URL',
      rowContent: ({ pdf_url }) => (
        <IconButton
          size="small"
          aria-label="Copy URL"
          onClick={() => copyToClipboard(pdf_url)}
        >
          <ContentCopy fontSize="small" />
        </IconButton>
      ),
      skipExport: true,
    },
  ]

  return (
    <Box padding="10px">
      <Typography variant="h6" color="error" gutterBottom>
        Errors ({errors.length})
      </Typography>
      <Box mb={2}>
        {errorGroups.map(([message, { count }]) => (
          <Box key={message} mb={0.5}>
            <Typography variant="body2" color="text.secondary">
              <strong>{count}x</strong> {message}
            </Typography>
          </Box>
        ))}
      </Box>
      <Table
        title="Translation Errors"
        items={errors}
        columns={errorColumns}
        overrideHeight={220}
      />
    </Box>
  )
}

function SkippedUrlsSection({ urls }: { urls: string[] }) {
  return (
    <Box padding="10px">
      <Typography variant="subtitle1" gutterBottom>
        Skipped URLs ({urls.length})
      </Typography>
      {urls.map((url) => (
        <Box key={url} mb={0.5} display="flex" alignItems="center" gap={1}>
          <Typography variant="body2" sx={{ wordBreak: 'break-word' }}>
            {extractFileName(url)}
          </Typography>
          <IconButton
            size="small"
            aria-label="Copy skipped URL"
            onClick={() => copyToClipboard(url)}
          >
            <ContentCopy fontSize="small" />
          </IconButton>
        </Box>
      ))}
    </Box>
  )
}

function TranslationResultsTable({
  results,
}: {
  results: PdfTranslationResult[]
}) {
  const sortedResults = useMemo(
    () => [...results].sort((a, b) => b.pages - a.pages),
    [results],
  )

  const columns: Column<PdfTranslationResult>[] = [
    {
      title: 'PDF',
      rowContent: ({ original_url }) => (
        <Typography
          variant="body2"
          component="a"
          href={original_url}
          target="_blank"
          rel="noopener noreferrer"
          sx={{ wordBreak: 'break-word' }}
        >
          {extractFileName(original_url)}
        </Typography>
      ),
    },
    {
      title: 'Pages',
      rowContent: ({ pages }) => pages,
    },
    {
      title: 'Blocks',
      rowContent: ({ blocks_extracted, blocks_translated }) =>
        `${blocks_translated}/${blocks_extracted}`,
    },
    {
      title: 'Overflow',
      rowContent: ({ overflow_blocks }) =>
        overflow_blocks.length > 0 ? (
          <Chip
            label={overflow_blocks.length}
            color="warning"
            size="small"
            variant="outlined"
          />
        ) : (
          '-'
        ),
    },
    {
      title: 'Tokens',
      rowContent: ({ telemetry }) =>
        (telemetry.total_tokens ?? 0).toLocaleString(),
    },
    {
      title: 'Translated PDF',
      rowContent: ({ translated_url }) => (
        <Button
          size="small"
          variant="text"
          href={translated_url}
          target="_blank"
          rel="noopener noreferrer"
        >
          Open
        </Button>
      ),
      skipExport: true,
    },
    {
      title: 'Copy Original',
      rowContent: ({ original_url }) => (
        <IconButton
          size="small"
          aria-label="Copy original URL"
          onClick={() => copyToClipboard(original_url)}
        >
          <ContentCopy fontSize="small" />
        </IconButton>
      ),
      skipExport: true,
    },
  ]

  return (
    <Box padding="10px">
      <Typography variant="subtitle1" gutterBottom>
        Translation Results ({results.length})
      </Typography>
      <Table
        title="Translated PDFs"
        items={sortedResults}
        columns={columns}
        overrideHeight={400}
      />
    </Box>
  )
}

function OverflowBlocksSection({
  results,
}: {
  results: PdfTranslationResult[]
}) {
  const [expanded, setExpanded] = React.useState(false)

  const allOverflows = useMemo(() => {
    return results.flatMap((r) =>
      r.overflow_blocks.map((block) => ({
        ...block,
        pdf_name: extractFileName(r.original_url),
        original_url: r.original_url,
      })),
    )
  }, [results])

  if (allOverflows.length === 0) return null

  const columns: Column<OverflowRow>[] = [
    {
      title: 'PDF',
      rowContent: ({ pdf_name }) => (
        <Typography variant="body2">{pdf_name}</Typography>
      ),
    },
    {
      title: 'Page',
      rowContent: ({ page_num }) => page_num + 1,
    },
    {
      title: 'Text',
      rowContent: ({ original_text }) => (
        <Typography
          variant="body2"
          sx={{
            maxWidth: 400,
            overflow: 'hidden',
            textOverflow: 'ellipsis',
            whiteSpace: 'nowrap',
          }}
        >
          {original_text}
        </Typography>
      ),
    },
    {
      title: 'Font Size',
      rowContent: ({ original_size }) =>
        typeof original_size === 'number' ? original_size.toFixed(1) : '-',
    },
    {
      title: 'Overflow RC',
      rowContent: ({ overflow_rc }) => overflow_rc.toFixed(2),
    },
  ]

  return (
    <Box padding="10px">
      <Box display="flex" alignItems="center" gap={1}>
        <Typography variant="subtitle1">
          Overflow Blocks ({allOverflows.length})
        </Typography>
        <IconButton
          size="small"
          onClick={() => setExpanded(!expanded)}
          aria-label={
            expanded ? 'Collapse overflow blocks' : 'Expand overflow blocks'
          }
        >
          {expanded ? <ExpandLess /> : <ExpandMore />}
        </IconButton>
      </Box>
      <Typography variant="body2" color="text.secondary" mb={1}>
        Text blocks that did not fit in the translated PDF layout
      </Typography>
      <Collapse in={expanded}>
        <Table
          title="Overflow Blocks"
          items={allOverflows}
          columns={columns}
          overrideHeight={300}
        />
      </Collapse>
    </Box>
  )
}
